import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import UIElement from '@ckeditor/ckeditor5-engine/src/view/uielement';
import { toWidget } from '@ckeditor/ckeditor5-widget/src/utils';
import Widget from '@ckeditor/ckeditor5-widget/src/widget';
import {
  INSERT_RESOURCE_LIST_SECTION_COMMAND,
  UPDATE_RESOURCE_LIST_SECTION_COMMAND,
  ResourceSectionSchema,
  ResourceSectionIdAttr,
  ResourceIdsAttr,
  ResourceSectionTypeAttr,
  ResourceSectionFilterTypeAttr,
  ResourceSectionFilterRangeAttr,
} from './constants';
import { InsertResourceListSectionCommand } from './insert-resource-list-section-command';
import styles from './resource-list-section.module.css';
import { ResourcelistSectionPluginConfiguration } from './types';
import { UpdateResourceListSectionCommand } from './update-resource-list-section-command';

export class ResourceListSection extends Plugin {
  static get requires() {
    return [Widget];
  }

  static get pluginName(): string {
    return 'ResourceListSection';
  }

  init() {
    this.defineSchema();
    this.defineConverters();
    this.editor.commands.add(
      INSERT_RESOURCE_LIST_SECTION_COMMAND,
      new InsertResourceListSectionCommand(this.editor),
    );
    this.editor.commands.add(
      UPDATE_RESOURCE_LIST_SECTION_COMMAND,
      new UpdateResourceListSectionCommand(this.editor),
    );
  }

  private defineSchema() {
    const { schema } = this.editor.model;

    schema.register(ResourceSectionSchema, {
      // Behaves like a self-contained object (e.g. an image).
      isObject: true,

      // Allow in places where other blocks are allowed (e.g. directly in the root).
      allowWhere: '$block',
      allowAttributes: [
        ResourceSectionIdAttr,
        ResourceIdsAttr,
        ResourceSectionTypeAttr,
        ResourceSectionFilterTypeAttr,
        ResourceSectionFilterRangeAttr,
      ],
    });
  }

  private defineConverters() {
    const { editor } = this;
    const { conversion } = editor;
    const renderResourceListSection =
      editor.config.get<ResourcelistSectionPluginConfiguration>(
        ResourceSectionSchema,
      ).renderer;

    conversion.for('upcast').elementToElement({
      view: {
        name: 'section',
        classes: [ResourceSectionSchema],
      },
      model: (viewElement, { writer: modelWriter }) => {
        const resourceSectionId = viewElement.getAttribute(
          `data-${ResourceSectionIdAttr}`,
        );
        const resourceSectionType = viewElement.getAttribute(
          `data-${ResourceSectionTypeAttr}`,
        );

        return modelWriter.createElement(ResourceSectionSchema, {
          [ResourceIdsAttr]: viewElement.getAttribute(
            `data-${ResourceIdsAttr}`,
          ),
          [ResourceSectionIdAttr]: resourceSectionId,
          [ResourceSectionTypeAttr]: resourceSectionType,
          [ResourceSectionFilterTypeAttr]: viewElement.getAttribute(
            `data-${ResourceSectionFilterTypeAttr}`,
          ),
          [ResourceSectionFilterRangeAttr]: viewElement.getAttribute(
            `data-${ResourceSectionFilterRangeAttr}`,
          ),
        });
      },
    });

    conversion.for('editingDowncast').elementToElement({
      model: ResourceSectionSchema,
      view: (modelElement, { writer: viewWriter }) => {
        const resourceIds = modelElement.getAttribute(ResourceIdsAttr);

        const resourceSectionId = modelElement.getAttribute(
          ResourceSectionIdAttr,
        );
        const resourceSectionType = modelElement.getAttribute(
          ResourceSectionTypeAttr,
        );
        const resourceSectionFilterType = modelElement.getAttribute(
          ResourceSectionFilterTypeAttr,
        );
        const resourceSectionFilterRange = modelElement.getAttribute(
          ResourceSectionFilterRangeAttr,
        );

        const reactWrapper = viewWriter.createUIElement(
          'section',
          {
            class: `${ResourceSectionSchema}__react-wrapper`,
          },
          function (this: UIElement, domDocument: any) {
            const domElement = this.toDomElement(domDocument);

            renderResourceListSection(
              {
                resourceIds,
                resourceSectionId,
                resourceSectionType,
                resourceSectionFilterType,
                resourceSectionFilterRange,
              },
              domElement,
              editor,
            );

            return domElement;
          },
        );

        const resourceSectionView = viewWriter.createContainerElement(
          'section',
          {
            class: `${ResourceSectionSchema} ${styles.section}`,
            [`data-${ResourceIdsAttr}`]: resourceIds,
            [`data-${ResourceSectionIdAttr}`]: resourceSectionId,
            [`data-${ResourceSectionTypeAttr}`]: resourceSectionType,
            [`data-${ResourceSectionFilterTypeAttr}`]:
              resourceSectionFilterType,
            [`data-${ResourceSectionFilterRangeAttr}`]:
              resourceSectionFilterRange,
            'data-cke-ignore-events': true,
          },
          [reactWrapper],
        );

        return toWidget(resourceSectionView, viewWriter);
      },
    });

    conversion.for('dataDowncast').elementToElement({
      model: ResourceSectionSchema,
      view: (modelItem, { writer: viewWriter }) => {
        const resourceIds = modelItem.getAttribute(ResourceIdsAttr);
        const resourceSectionId = modelItem.getAttribute(ResourceSectionIdAttr);
        const resourceSectionType = modelItem.getAttribute(
          ResourceSectionTypeAttr,
        );
        const resourceSectionFilterType = modelItem.getAttribute(
          ResourceSectionFilterTypeAttr,
        );
        const resourceSectionFilterRange = modelItem.getAttribute(
          ResourceSectionFilterRangeAttr,
        );

        const resourceSectionEl = viewWriter.createRawElement('section', {
          class: `${styles.section} ${styles.section_loading}`,
        });

        const resourceSectionView = viewWriter.createContainerElement(
          'section',
          {
            class: ResourceSectionSchema,
            [`data-${ResourceIdsAttr}`]: resourceIds,
            [`data-${ResourceSectionIdAttr}`]: resourceSectionId,
            [`data-${ResourceSectionTypeAttr}`]: resourceSectionType,
            [`data-${ResourceSectionFilterTypeAttr}`]:
              resourceSectionFilterType,
            [`data-${ResourceSectionFilterRangeAttr}`]:
              resourceSectionFilterRange,
          },
          [resourceSectionEl],
        );

        return resourceSectionView;
      },
    });
  }
}
